/**
 * Document API Hooks
 * BRD-001: Document Upload, Storage & Organization
 *
 * React Query hooks for document operations.
 * Connected to real backend API at /api/v1/documents.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@myvault/shared';
import type { Document, Classification } from '@myvault/shared';

const API_BASE = '/api/v1/documents';

/**
 * Get authorization headers for API requests
 */
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('myvault_access_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Types matching backend API response
interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface ApiDocumentListItem {
  id: string;
  external_id: string;
  title: string;
  original_filename: string;
  mime_type: string;
  file_size_bytes: number;
  data_classification: string;
  category_id: string | null;
  status: string;
  is_private: boolean;
  created_at: string;
  updated_at: string | null;
}

interface ApiPaginatedResponse {
  data: ApiDocumentListItem[];
  pagination: PaginationMeta;
}

// Extended document type with additional fields from API
interface ExtendedDocument extends Document {
  original_filename?: string;
  status?: string;
}

interface DocumentListResponse {
  documents: ExtendedDocument[];
  pagination: PaginationMeta;
}

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_size: number;
  created_at: string;
  created_by: string;
  change_notes?: string;
}

// Query keys factory
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (params: DocumentListParams) => [...documentKeys.lists(), params] as const,
  details: () => [...documentKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentKeys.details(), id] as const,
  versions: (id: string) => [...documentKeys.detail(id), 'versions'] as const,
  activity: (id: string) => [...documentKeys.detail(id), 'activity'] as const,
  shares: (id: string) => [...documentKeys.detail(id), 'shares'] as const,
  categories: () => [...documentKeys.all, 'categories'] as const,
  stats: () => [...documentKeys.all, 'stats'] as const,
};

// Types
export interface DocumentListParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  category?: string;
  fileType?: string;
  dateRange?: string;
}

export interface DocumentActivity {
  id: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  action: string;
  timestamp: string;
  details?: string;
}

export interface DocumentShare {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  permissionLevel: 'view' | 'download' | 'edit';
  createdAt: string;
  expiresAt?: string;
}

export interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

/**
 * Map API document to frontend Document type
 */
function mapApiDocumentToDocument(apiDoc: ApiDocumentListItem): ExtendedDocument {
  return {
    id: apiDoc.id,
    title: apiDoc.title,
    mime_type: apiDoc.mime_type,
    size: apiDoc.file_size_bytes,
    category: apiDoc.category_id || 'other',
    tags: [],
    classification: (apiDoc.data_classification || 'internal') as Classification,
    created_at: apiDoc.created_at,
    updated_at: apiDoc.updated_at || apiDoc.created_at,
    original_filename: apiDoc.original_filename,
    status: apiDoc.status,
  };
}

// Hooks

/**
 * Fetch paginated document list from API
 */
export function useDocuments(params: DocumentListParams = {}) {
  return useQuery({
    queryKey: documentKeys.list(params),
    queryFn: async (): Promise<DocumentListResponse> => {
      // Build query string
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', params.page.toString());
      if (params.limit) queryParams.set('limit', params.limit.toString());
      if (params.sort) queryParams.set('sort', params.sort);
      if (params.order) queryParams.set('order', params.order);
      if (params.search) queryParams.set('search', params.search);
      if (params.category) queryParams.set('category_id', params.category);

      const url = `${API_BASE}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await fetch(url, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Je bent niet ingelogd');
        }
        throw new Error('Documenten konden niet worden opgehaald');
      }

      const apiResponse: ApiPaginatedResponse = await response.json();

      // Map API response to frontend types
      return {
        documents: apiResponse.data.map(mapApiDocumentToDocument),
        pagination: apiResponse.pagination,
      };
    },
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Fetch single document details
 */
export function useDocument(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.detail(id!),
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/${id}`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document niet gevonden');
        }
        if (response.status === 401) {
          throw new Error('Je bent niet ingelogd');
        }
        throw new Error('Document kon niet worden opgehaald');
      }

      const data = await response.json();

      // Map API response to Document type
      return {
        id: data.id,
        title: data.title,
        mime_type: data.mime_type,
        size: data.file_size_bytes,
        category: data.category_id || 'other',
        tags: [],
        classification: data.data_classification || 'internal',
        created_at: data.created_at,
        updated_at: data.updated_at,
        original_filename: data.original_filename,
        file_extension: data.file_extension,
        storage_path: data.storage_path,
        status: data.status,
        description: data.description,
      } as Document;
    },
    enabled: !!id,
    staleTime: 60000, // 1 minute
  });
}

/**
 * Fetch document activity/audit history from API
 */
export function useDocumentActivity(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.activity(id!),
    queryFn: async (): Promise<DocumentActivity[]> => {
      const response = await fetch(`${API_BASE}/${id}/audit`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error('Audit log kon niet worden opgehaald');
      }

      const data = await response.json();

      // Map API audit entries to DocumentActivity
      return (data.entries || []).map((entry: { id: string; user_id: string; user_name?: string; action: string; created_at: string; details?: string }) => ({
        id: entry.id,
        userId: entry.user_id,
        userName: entry.user_name || 'Onbekende gebruiker',
        action: entry.action,
        timestamp: entry.created_at,
        details: entry.details,
      }));
    },
    enabled: !!id,
  });
}

/**
 * Fetch document version history
 * Note: Version history endpoint not yet implemented in backend
 */
export function useDocumentVersions(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.versions(id!),
    queryFn: async (): Promise<DocumentVersion[]> => {
      // TODO: Connect to real API when versioning is implemented
      // For now return empty array - documents don't have versions yet
      return [];
    },
    enabled: !!id,
  });
}

/**
 * Fetch document shares from API
 */
export function useDocumentShares(id: string | undefined) {
  return useQuery({
    queryKey: documentKeys.shares(id!),
    queryFn: async (): Promise<DocumentShare[]> => {
      const response = await fetch(`${API_BASE}/${id}/shares`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error('Shares konden niet worden opgehaald');
      }

      const data = await response.json();

      // Map API shares to DocumentShare
      return (data.shares || []).map((share: { id: string; user_id: string; user_name?: string; user_email?: string; permission: string; created_at: string; expires_at?: string }) => ({
        id: share.id,
        userId: share.user_id,
        userName: share.user_name || 'Onbekende gebruiker',
        userEmail: share.user_email || '',
        permissionLevel: share.permission as 'view' | 'download' | 'edit',
        createdAt: share.created_at,
        expiresAt: share.expires_at,
      }));
    },
    enabled: !!id,
  });
}

/**
 * Fetch categories with counts
 * Note: Categories endpoint uses /api/v1/categories
 */
export function useCategories() {
  return useQuery({
    queryKey: documentKeys.categories(),
    queryFn: async (): Promise<Category[]> => {
      const response = await fetch('/api/v1/categories', {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // Return default categories if endpoint not available
        return [
          { id: 'all', name: 'Alle documenten', icon: 'folder', count: 0 },
        ];
      }

      const data = await response.json();

      return (data.categories || data || []).map((cat: { id: string; name: string; icon?: string; document_count?: number }) => ({
        id: cat.id,
        name: cat.name,
        icon: cat.icon || 'folder',
        count: cat.document_count || 0,
      }));
    },
    staleTime: 60000, // 1 minute
  });
}

/**
 * Upload documents mutation using presigned URL flow
 */
export function useUploadDocuments() {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: async ({
      files,
      category,
    }: {
      files: File[];
      category?: string;
      folderId?: string;
    }) => {
      const uploadedDocs: Array<{ id: string }> = [];

      for (const file of files) {
        // Step 1: Get presigned upload URL
        const urlResponse = await fetch(`${API_BASE}/upload-url`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type,
            file_size: file.size,
            category_id: category,
          }),
        });

        if (!urlResponse.ok) {
          throw new Error(`Upload URL aanvragen mislukt voor ${file.name}`);
        }

        const { upload_url, document_id, fields } = await urlResponse.json();

        // Step 2: Upload file to storage (MinIO/S3)
        const formData = new FormData();
        if (fields) {
          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value as string);
          });
        }
        formData.append('file', file);

        const uploadResponse = await fetch(upload_url, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Bestand uploaden mislukt voor ${file.name}`);
        }

        // Step 3: Confirm upload completion
        const completeResponse = await fetch(`${API_BASE}/upload-complete`, {
          method: 'POST',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            document_id,
            title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for title
          }),
        });

        if (!completeResponse.ok) {
          throw new Error(`Upload bevestigen mislukt voor ${file.name}`);
        }

        const doc = await completeResponse.json();
        uploadedDocs.push(doc);
      }

      return uploadedDocs;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      success('Upload voltooid', `${data.length} document(en) succesvol geüpload`);
    },
    onError: (err) => {
      error('Upload mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}

/**
 * Delete document mutation
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document niet gevonden');
        }
        throw new Error('Document verwijderen mislukt');
      }

      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      success('Document verwijderd', 'Het document is succesvol verwijderd');
    },
    onError: (err) => {
      showError('Verwijderen mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}

/**
 * Delete multiple documents mutation (bulk delete)
 */
export function useDeleteDocuments() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await fetch(`${API_BASE}/bulk/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ document_ids: ids }),
      });

      if (!response.ok) {
        throw new Error('Documenten verwijderen mislukt');
      }

      const result = await response.json();
      return { ids, ...result };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      success('Documenten verwijderd', `${data.ids.length} document(en) succesvol verwijderd`);
    },
    onError: (err) => {
      showError('Verwijderen mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}

/**
 * Update document mutation
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Document>;
    }) => {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: updates.title,
          category_id: updates.category,
          data_classification: updates.classification,
        }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document niet gevonden');
        }
        throw new Error('Document bijwerken mislukt');
      }

      const data = await response.json();
      return { id: data.id, ...updates };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
      success('Document bijgewerkt', 'De wijzigingen zijn opgeslagen');
    },
    onError: (err) => {
      showError('Bijwerken mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}

/**
 * Download document - gets presigned download URL
 */
export function useDownloadDocument() {
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`${API_BASE}/${id}/download`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Document niet gevonden');
        }
        throw new Error('Download URL ophalen mislukt');
      }

      const data = await response.json();
      return { id, downloadUrl: data.download_url };
    },
    onSuccess: (data) => {
      // Open download URL in new tab/window
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
      success('Download gestart', 'Het document wordt gedownload');
    },
    onError: (err) => {
      showError('Download mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}

/**
 * Share document mutation
 */
export function useShareDocument() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: async ({
      documentId,
      userIds,
      permissionLevel,
      expiresAt,
    }: {
      documentId: string;
      userIds?: string[];
      email?: string;
      permissionLevel: 'view' | 'download' | 'edit';
      expiresAt?: string;
    }) => {
      const response = await fetch(`${API_BASE}/${documentId}/share`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_ids: userIds || [],
          permission: permissionLevel,
          expires_at: expiresAt,
        }),
      });

      if (!response.ok) {
        throw new Error('Document delen mislukt');
      }

      const data = await response.json();
      return { documentId, ...data };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.shares(data.documentId) });
      success('Document gedeeld', 'Het document is succesvol gedeeld');
    },
    onError: (err) => {
      showError('Delen mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}

/**
 * Revoke share mutation
 */
export function useRevokeShare() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  return useMutation({
    mutationFn: async ({ documentId, userId }: { documentId: string; userId: string; shareId?: string }) => {
      const response = await fetch(`${API_BASE}/${documentId}/share/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Toegang intrekken mislukt');
      }

      return { documentId, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.shares(data.documentId) });
      success('Toegang ingetrokken', 'De gebruiker heeft geen toegang meer tot dit document');
    },
    onError: (err) => {
      showError('Intrekken mislukt', err instanceof Error ? err.message : 'Er is een fout opgetreden');
    },
  });
}
